import { Dispatch, useRef, useState } from 'react';
import { SetStateAction, useAtomValue } from 'jotai';

import { PictureIcon, CloudUpload } from 'components/Commons/icons';
import Goback from 'components/Commons/goback';

import { languageModeAtom } from 'src/atoms';
import useModal from 'hooks/useModal';

const imageFormatText = 'png, jpg, jpeg, gif';
const modelingFormatText = 'gltf, glb';

const scripts = {
  KR: [
    '새로운 포스팅',
    '이미지',
    '3D 모델링',
    '이미지 포멧이 적절하지 않습니다.',
    '3D 모델 포멧이 적절하지 않습니다.',
    '파일만 업로드 가능합니다.',
    '이미지 용량을 초과하였습니다.',
    '최대 10MB 이하',
    '의 이미지만 업로드 가능합니다.',
    '3D 모델 용량을 초과하였습니다.',
    '최대 100MB 이하',
    '의 3D 모델만 업로드 가능합니다.',
  ],
  EN: [
    'New Posting',
    'Image',
    '3D modeling',
    'Improper image format.',
    '3D model format is not suitable.',
    'files can be uploaded.',
    'Image capacity exceeded.',
    'of images can be uploaded.',
    'maximum 10MB or less',
    'Exceeded 3D model capacity.',
    'maximum 100MB or less',
    'of 3D models can be uploaded.',
  ],
  JP: [
    '新しい投稿',
    '画像',
    '3Dモデリング',
    '画像フォーマットが適切ではありません。',
    '3Dモデルフォーマットは適切ではありません。',
    'ファイルのみアップロード可能です。',
    '画像容量を超えました。',
    '最大10MB以下',
    'の画像のみアップロード可能です。',
    '3Dモデル容量を超えました。',
    '最大100MB以下',
    'の3Dモデルのみアップロード可能です。',
  ],
};

export function UploadModal() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }
  return (
    <>
      <button id='URLModalBtn' onClick={openModal} className='ml-4'>
        <CloudUpload />
      </button>
      {isOpen && (
        <div
          className='flex flex-col items-center  absolute bottom-0 left-1  z-10 bg-white w-screen text-zinc-600 rounded-t-lg font-light border border-neutral-300 border-b-0'
          style={{ width: '98%' }}>
          <button
            className='pt-4'
            onClick={() => {
              closeModal();
            }}>
            <svg width='41' height='4' viewBox='0 0 41 4' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <rect x='0.356201' width='40' height='4' rx='2' fill='#808080' />
            </svg>
          </button>
          <button className='flex gap-1.5 px-3 py-3 border-b border-zinc-100 w-full'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M5.74576 14.1724V17.5862L11.8475 21M5.74576 14.1724L3 12.3103L6.35593 9.51724M5.74576 14.1724L8.49153 16.0345L11.8475 13.2414M11.8475 21V13.2414M11.8475 21L18.1017 17.2759V14.1724M11.8475 13.2414L17.339 9.51724M11.8475 13.2414L6.35593 9.51724M11.8475 13.2414L15.5085 16.0345L18.1017 14.1724M17.339 9.51724L11.8475 5.7931M17.339 9.51724L20.6949 12.3103L18.1017 14.1724M17.339 9.51724L21 6.41379L15.5085 3L11.8475 5.7931M11.8475 5.7931L6.35593 9.51724M11.8475 5.7931L8.18644 3L3 6.41379L6.35593 9.51724'
                stroke='black'
                strokeLinejoin='round'
              />
            </svg>
            <div> Dropbox</div>
          </button>
          <button className='flex gap-1.5 px-3 py-3 border-b border-zinc-100 w-full'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M3 15.5L5.76923 20.5H18.2308M3 15.5L9.23077 3.5H14.5385M3 15.5H8.07692M18.2308 20.5L21 15.5L14.5385 3.5M18.2308 20.5L15.6923 15.5M14.5385 3.5L8.07692 15.5M15.6923 15.5L11.8462 8.5M15.6923 15.5H8.07692'
                stroke='black'
                strokeLinejoin='round'
              />
            </svg>

            <div>Google Drive</div>
          </button>
          <button className='flex gap-1.5 px-3 py-3 border-b border-zinc-100 w-full'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M16.8979 10.4355L16.7131 9.97091L16.8979 10.4355ZM3.16497 11.5L2.69087 11.3411L3.16497 11.5ZM8.69383 8.83872L8.93568 8.4011L8.69383 8.83872ZM20.2866 15.7251L20.6673 16.0492L20.2866 15.7251ZM17.2546 10.3435L17.3336 10.8372L17.2546 10.3435ZM6.91032 17.5H17.2546V16.5H6.91032V17.5ZM16.7131 9.97091C15.9973 10.2557 7.96839 13.5215 4.0466 15.1175L4.42354 16.0438C8.34916 14.4462 16.3718 11.183 17.0828 10.9001L16.7131 9.97091ZM3.83582 15.8817C4.04924 16.1647 4.44086 16.5608 4.95608 16.8893C5.47282 17.2188 6.14513 17.5 6.91032 17.5V16.5C6.3914 16.5 5.90443 16.3081 5.49374 16.0462C5.08154 15.7833 4.77759 15.4697 4.63431 15.2796L3.83582 15.8817ZM4.60416 15.2434C4.34729 14.9623 3.9732 14.4234 3.73284 13.7639C3.49334 13.1067 3.4006 12.3705 3.63906 11.6589L2.69087 11.3411C2.35861 12.3327 2.50368 13.3116 2.79328 14.1063C3.08203 14.8986 3.52834 15.5485 3.86598 15.918L4.60416 15.2434ZM3.63906 11.6589C3.93821 10.7661 4.7916 9.96527 5.80653 9.49136C6.83745 9.00999 7.85407 8.9459 8.45198 9.27633L8.93568 8.4011C7.92843 7.84444 6.53732 8.04648 5.38345 8.58527C4.21359 9.13152 3.10512 10.1049 2.69087 11.3411L3.63906 11.6589ZM17.2546 17.5C18.1542 17.5 19.6992 17.1863 20.6673 16.0492L19.9059 15.4009C19.2053 16.2239 18.0065 16.5 17.2546 16.5V17.5ZM20.6673 16.0492C21.1747 15.4532 21.5 14.6558 21.5 13.629H20.5C20.5 14.4285 20.2528 14.9935 19.9059 15.4009L20.6673 16.0492ZM8.45198 9.27633C9.7258 9.98032 16.6936 14.1501 20.0291 16.1537L20.5441 15.2965C17.2212 13.3005 10.2301 9.11647 8.93568 8.4011L8.45198 9.27633ZM8.08763 8.84782C8.3911 8.09311 9.59519 6.5 11.9041 6.5V5.5C9.07659 5.5 7.56976 7.45527 7.15982 8.47476L8.08763 8.84782ZM21.5 13.629C21.5 11.9531 20.7718 10.9178 19.8259 10.3559C18.9175 9.81632 17.8579 9.7407 17.1757 9.84978L17.3336 10.8372C17.8332 10.7574 18.6463 10.8183 19.3151 11.2157C19.9465 11.5907 20.5 12.2926 20.5 13.629H21.5ZM17.1757 9.84978C17.0096 9.87634 16.851 9.91605 16.7131 9.97091L17.0828 10.9001C17.1347 10.8794 17.2183 10.8557 17.3336 10.8372L17.1757 9.84978ZM11.9041 6.5C14.8426 6.5 16.4389 8.41733 16.761 10.4228L17.7483 10.2642C17.357 7.82767 15.3862 5.5 11.9041 5.5V6.5Z'
                fill='black'
              />
            </svg>

            <div>One Drive</div>
          </button>
        </div>
      )}
    </>
  );
}

export function ImageUpload({
  setFile,
  setProgress,
}: {
  setFile: Dispatch<SetStateAction<{ element: File; itemtype: number; original?: File | null } | null>>;
  setProgress: Dispatch<SetStateAction<number>>;
}) {
  const languageMode = useAtomValue(languageModeAtom);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const modelingInputRef = useRef<HTMLInputElement>(null);
  const { openAlertModal } = useModal();

  return (
    <>
      <div className='flex justify-center h-11 shrink-0'>
        <div className='grow basis-0 justify-end'>
          <div className='lg:hidden'>
            <Goback />
          </div>
        </div>
        <div className='flex items-center text-xl font-semibold'>{scripts[languageMode][0]}</div>
        <div className='flex grow basis-0 justify-end'></div>
      </div>
      <div className='flex flex-col'>
        <div
          className='w-full'
          onTouchStart={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f3f3';
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          onClick={() => {
            imageInputRef.current?.click();
          }}>
          <div className='flex flex-row justify-between h-11 px-4 border-b-1'>
            <h4 className='flex items-center text-lg'>{scripts[languageMode][1]}</h4>
            <div className='flex items-center'>
              <input
                className='hidden'
                type='file'
                accept='image/*'
                ref={imageInputRef}
                onChange={(e) => {
                  if (e.currentTarget.files) {
                    let name = e.currentTarget.files[0].name;
                    let type = name.split('.')[name.split('.').length - 1];
                    if (
                      type.toUpperCase() === 'PNG' ||
                      type.toUpperCase() === 'JPG' ||
                      type.toUpperCase() === 'JPEG' ||
                      type.toUpperCase() === 'GIF'
                    ) {
                      if (e.currentTarget.files[0].size <= 1024 * 1024 * 10) {
                        setFile({ element: e.currentTarget.files[0], itemtype: 1, original: e.currentTarget.files[0] });
                        setProgress(2);
                      } else {
                        e.preventDefault();
                        openAlertModal([
                          { type: 'NORMAL', message: scripts[languageMode][6] },
                          { type: 'NEW_LINE' },
                          { type: 'IMPORTANT', message: scripts[languageMode][7] },
                          { type: 'NORMAL', message: scripts[languageMode][8] },
                        ]);
                      }
                    } else {
                      e.preventDefault();
                      openAlertModal([
                        { type: 'NORMAL', message: scripts[languageMode][3] },
                        { type: 'NEW_LINE' },
                        { type: 'IMPORTANT', message: imageFormatText },
                        { type: 'NORMAL', message: scripts[languageMode][5] },
                      ]);
                    }
                  }
                }}
              />
              <button className='ml-4'>
                <PictureIcon />
              </button>
              {/* <UploadModal /> */}
            </div>
          </div>
        </div>
        <div
          className='w-full'
          onTouchStart={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f3f3';
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          onClick={() => {
            modelingInputRef.current?.click();
          }}>
          <div className='flex flex-row justify-between h-11 px-4 border-b-1'>
            <h4 className='flex items-center text-lg'>{scripts[languageMode][2]}</h4>
            <div className='flex items-center'>
              <input
                className='hidden'
                type='file'
                ref={modelingInputRef}
                onChange={(e) => {
                  if (e.currentTarget.files) {
                    let name = e.currentTarget.files[0].name;
                    let type = name.split('.')[name.split('.').length - 1];
                    if (type.toUpperCase() === 'GLTF' || type.toUpperCase() === 'GLB') {
                      if (e.currentTarget.files[0].size <= 1024 * 1024 * 100) {
                        setFile({ element: e.currentTarget.files[0], itemtype: 2 });
                        setProgress(2);
                      } else {
                        e.preventDefault();
                        openAlertModal([
                          { type: 'NORMAL', message: scripts[languageMode][9] },
                          { type: 'NEW_LINE' },
                          { type: 'IMPORTANT', message: scripts[languageMode][10] },
                          { type: 'NORMAL', message: scripts[languageMode][11] },
                        ]);
                      }
                    } else {
                      e.preventDefault();
                      openAlertModal([
                        { type: 'NORMAL', message: scripts[languageMode][4] },
                        { type: 'NEW_LINE' },
                        { type: 'IMPORTANT', message: modelingFormatText },
                        { type: 'NORMAL', message: scripts[languageMode][5] },
                      ]);
                    }
                  }
                }}
              />
              <button className='ml-4'>
                <PictureIcon />
              </button>
              {/* <UploadModal /> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
